import React from 'react';
import { CustomProvider, renderComponent } from 'react/utils/renderUtils';
import { getResourceTo } from 'react/utils/utils';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import TelematicsMultiSource from 'react/components/Telematics/TelematicsMultiSource';
import TelemeticEmptyState from 'react/components/Telematics/TelemeticEmptyState';
import Loading from 'react/components/Loading/Loading';

const defaultTelematicsCapabilitiesList = [
  {
    id: 1,
    key: 'speed',
    description: 'Greenmile.Telematic.speedDescription',
    type: 'DECIMAL'
  }
];

export const TELEMETICS_DOMAINS = {
  ROUTE_DETAILS: 'routeDetails',
  TRACK_EQUIPMENTS: 'trackEquipments'
};

export const renderTelematics = (
  positionSources,
  telematicContainer,
  capabilitiesFromTrackCompany = [],
  sessionData = null
) => {
  const telematicSourceData = positionSources?.mapToTelematics();
  renderComponent(
    <CustomProvider>
      <TelematicsMultiSource
        sources={telematicSourceData}
        capabilitiesFromTrackCompany={capabilitiesFromTrackCompany}
        sessionData={sessionData}
      />
    </CustomProvider>,
    telematicContainer
  );
};

const renderTelematicsLoading = (telematicContainer) => {
  renderComponent(
    <div
      style={{
        padding: 20,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
      }}
    >
      <Loading
        message={getResourceTo('loading.message')}
        isVisible
        width={100}
        height={100}
      />
    </div>,
    telematicContainer
  );
};

const renderTelematicsNoData = (telematicContainer) => {
  renderComponent(<TelemeticEmptyState />, telematicContainer);
};

export const fetchTelematicPropertyFromEquipment = async (equipmentId) => {
  if (!equipmentId) return null;
  const url =
    'Equipment/restrictions?criteria={"filters":["*","gpsProviderTelematicProfile.*","gpsProviderTelematicProfile.gpsProviderTelematicProps.*"]}';
  const payload = {
    criteriaChain: [
      {
        and: [
          {
            attr: 'id',
            eq: equipmentId
          }
        ]
      }
    ]
  };

  return defaultFetch(encodeURI(url), payload, 'POST');
};

export const getTelematicPropertyFromEquipmentAndRenderTelematics = async (
  equipmentId,
  positionSources,
  telematicContainer,
  sessionData = null,
  showLoading = true
) => {
  if (equipmentId) {
    showLoading && renderTelematicsLoading(telematicContainer);
    const data = await fetchTelematicPropertyFromEquipment(equipmentId);
    if (data?.error) {
      Monet.Configurations.errorNotification(null, data.error, null, null);
      renderTelematicsNoData(telematicContainer);
      return;
    }
    const equipment = new App.Models.Equipment(data[0]);
    const capabilities = [];
    if (equipment.get('gpsProviderTelematicProfile')) {
      const gpsProviderTelematicProfile = equipment.get(
        'gpsProviderTelematicProfile'
      );
      if (gpsProviderTelematicProfile?.gpsProviderTelematicProps?.length) {
        capabilities.push(
          ...gpsProviderTelematicProfile.gpsProviderTelematicProps
        );
      }
    }
    renderTelematics(positionSources, telematicContainer, capabilities, sessionData);
    return;
  }
  renderTelematicsNoData(telematicContainer);
};

export const mergeInputSourcesWithCapabilities = (
  sources,
  capabilitiesFromTrackCompany
) => {
  let changed = false;
  const mergedSources = sources?.map((source) => {
    const capabilities =
      source.sourceType === 'TELEMATICS'
        ? defaultTelematicsCapabilitiesList.concat(capabilitiesFromTrackCompany)
        : defaultTelematicsCapabilitiesList;
    if (
      !source.capabilities ||
      JSON.stringify(source.capabilities) !== JSON.stringify(capabilities)
    ) {
      changed = true;
      return {
        ...source,
        capabilities
      };
    }
    return source;
  });
  return changed ? mergedSources : sources;
};

export const getTelematicsStorageDetails = () => {
  const telematicsDetails = localStorage.getItem('telematics');
  return telematicsDetails ? JSON.parse(telematicsDetails) : {};
}
